import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { whereIs } from "./cityData";

export function CityChat({ open, onOpen, onClose, onSend }) {
  const [text, setText] = useState("");
  const ref = useRef(null);
  useEffect(() => { if (open) ref.current?.focus(); }, [open]);
  if (!open) {
    return (
      <button onClick={onOpen} className="cq-px cq-hud-btn flex items-center gap-1.5" data-testid="city-chat-open" title="Say something (T)">
        <MessageSquare size={12} /> <span className="hidden sm:inline">SAY</span>
      </button>
    );
  }
  const submit = (e) => { e.preventDefault(); const t = text.trim(); if (t) onSend(t); setText(""); onClose(); };
  return (
    <form onSubmit={submit} className="flex items-center gap-1 pointer-events-auto" data-testid="city-chat-form">
      <input
        ref={ref} value={text} onChange={(e) => setText(e.target.value)} maxLength={80} placeholder="say something…"
        onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Escape") { setText(""); onClose(); } }}
        className="cq-px text-[9px] bg-slate-950/90 border border-cyan-400/50 text-cyan-100 px-3 py-2 w-44 sm:w-64 outline-none focus:border-cyan-300"
        data-testid="city-chat-input"
      />
      <button type="submit" className="cq-px cq-hud-btn flex items-center gap-1" data-testid="city-chat-send"><Send size={12} /></button>
    </form>
  );
}

export function NameModal({ open, initial, hat, onSave, onClose }) {
  const [name, setName] = useState(initial || "");
  const [h, setH] = useState(hat || 0);
  useEffect(() => { setName(initial || ""); setH(hat || 0); }, [initial, hat, open]);
  if (!open) return null;
  const hats = ["NONE", "TOP HAT", "CAP", "CROWN", "BEANIE", "HALO"];
  return (
    <div className="absolute inset-0 z-50 bg-slate-950/80 flex items-center justify-center p-4" data-testid="city-name-modal" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); onSave(name.trim() || initial, h); }} className="w-full max-w-sm border-2 border-cyan-400/60 bg-[#070a1c] p-5 space-y-4">
        <p className="cq-px text-cyan-300 text-xs">YOUR DUCK</p>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={16} onKeyDown={(e) => e.stopPropagation()}
          className="cq-px text-[10px] w-full bg-slate-900 border border-cyan-400/40 text-white px-3 py-2 outline-none" data-testid="city-name-input" />
        <div className="grid grid-cols-3 gap-1">
          {hats.map((label, i) => (
            <button type="button" key={label} onClick={() => setH(i)} className={`cq-px text-[8px] px-2 py-2 border ${h === i ? "border-amber-300 text-amber-300" : "border-slate-600 text-slate-300"}`} data-testid={`city-hat-${i}`}>{label}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button type="submit" className="cq-px text-[9px] bg-cyan-400 text-slate-950 px-4 py-2" data-testid="city-name-save">SAVE</button>
          <button type="button" onClick={onClose} className="cq-px text-[9px] border border-slate-500 text-slate-200 px-4 py-2">CANCEL</button>
        </div>
      </form>
    </div>
  );
}

const AV = (name) => (String(name || "").toLowerCase().startsWith("irving") ? "/game/irving_front.webp" : "/game/duck_walk_0.webp");
export function WhoIsHere({ others, me, onGo, onWave, onText }) {
  const list = [...others].sort((a, b) => (a.scene === "street" ? -1 : 1) - (b.scene === "street" ? -1 : 1));
  return (
    <div className="space-y-3" data-testid="who-panel">
      <p className="cq-px text-[8px] text-slate-300">{others.length + 1} DUCKS IN THE CITY RIGHT NOW · you're <span className="text-amber-300">{me.name}</span>. Tap GO TO to walk straight to anyone — even inside a building.</p>
      {!others.length && <p className="font-mono text-[12px] text-slate-400">It's just you for the moment. Invite a friend — everyone in the city shares the same street, shops and rooms in real time.</p>}
      <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
        {list.map((o) => { const w = whereIs(o.scene); return (
          <div key={o.pid} className="flex items-center gap-3 border border-slate-700 bg-slate-950/60 px-3 py-2" data-testid={`who-${o.pid}`}>
            <img src={AV(o.name)} alt="" className="w-8 h-8 object-contain" style={{ imageRendering: "pixelated" }} />
            <div className="min-w-0 flex-1"><p className="cq-px text-[8px] text-white truncate">{o.name}{o.role === "client" && <span className="ml-2 text-[6px] text-emerald-300">CLIENT</span>}</p><p className="font-mono text-[11px] text-slate-400 truncate">{w.label}{o.car ? " · driving" : ""}{o.say ? ` · “${o.say}”` : ""}</p></div>
            <button onClick={() => onGo(o)} className="cq-px text-[7px] border border-amber-400 text-amber-300 px-2 py-1.5 hover:bg-amber-400/10" data-testid={`who-go-${o.pid}`}>GO TO</button>
            <button onClick={() => onWave(o)} className="cq-px text-[7px] border border-cyan-400 text-cyan-300 px-2 py-1.5 hover:bg-cyan-400/10 hidden sm:block" data-testid={`who-wave-${o.pid}`}>WAVE</button>
            <button onClick={() => onText(o)} className="cq-px text-[7px] border border-slate-500 text-slate-200 px-2 py-1.5 hover:bg-white/10 hidden sm:block" data-testid={`who-text-${o.pid}`}>TEXT</button>
          </div>); })}
      </div>
    </div>
  );
}
